import { useEffect, useMemo, useRef, useState } from "react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import useNotification from "../../hooks/notification.hook";
import ModalContainer from "./ModalContainer";
import { useTranslation } from "react-i18next";
import { createAnnotation, updateAnnotation } from "../../services/agendas.service";
import { ANNOTATION_TYPE_HELPER, ANNOTATION_TYPE_HELPER_ARRAY, cn } from "../../lib/utils";
import { formatDate } from "../../helper/date-formatter.helper";
import { useSessionStore } from "../../store/session.store";
import { checkScheduledEventAnnotation } from "../../services/supporto/schedule-events.services";

export default function CrudAnnotationModal({
	open,
	setOpen,
	annotationModel,
	agenda,
	onEdit = () => {},
	onInsert = () => {},
	scheduledEvents = [],
}) {
	const { t } = useTranslation();
	const checkScheduedEventSignal = useRef(new AbortController());

	const { user } = useSessionStore();

	const isScheduledEventEnabled = useMemo(() => ["admin"].includes(user?.role), [user?.role]);

	// Set message
	yup.setLocale({
		// use constant translation keys for messages without values
		mixed: {
			required: ({ path }) => t("common:errors.required", { field: t(`common:fields.${path}`) }),
		},
		number: {
			moreThan: ({ path, more }) =>
				t("common:errors.more_than", {
					field: t(`common:fields.${path}`),
					value: more,
				}),
			lessThan: ({ path, less }) =>
				t("common:errors.less_than", {
					field: t(`common:fields.${path}`),
					value: less,
				}),
		},
	});

	// Schema Yup
	const schema = yup.object().shape({
		date: yup.string().required(),
		type: yup.string().required(),
		title: yup.string().required(),
		scheduledEvent: yup.string(),
	});

	const {
		register,
		setValue,
		clearErrors,
		watch,
		handleSubmit,
		formState: { errors },
	} = useForm({ resolver: yupResolver(schema) });

	const { openNotification, helperErrorNotification } = useNotification();

	const [loading, setLoading] = useState(false);
	const [isAlertVisible, setIsAlertVisible] = useState(false);

	const checkScheduledEventUsed = async (value) => {
		if (!value) {
			setIsAlertVisible(false);
			return;
		}
		checkScheduedEventSignal.current.abort();
		checkScheduedEventSignal.current = new AbortController();
		try {
			const res = await checkScheduledEventAnnotation({
				params: {
					organizationId: agenda.organization_plan.organization_id,
					scheduleEventId: value,
				},
				config: {
					signal: checkScheduedEventSignal.current.signal,
				},
			});
			setIsAlertVisible(res.length > 0);
		} catch {}
	};

	/**
	 * Form Handler
	 * @param {form modal} data
	 */
	async function formHandler(data) {
		if (!loading) {
			setLoading(true);

			const sharedBody = {
				idCorp: agenda.organization_plan.organization_id,
				name: data.title,
				date: data.date,
				type: data.type,
				...(isScheduledEventEnabled && {
					scheduled_event_id: data.scheduledEvent || null,
				}),
			};

			if (!annotationModel?.id) {
				const body = {
					...sharedBody,
					idAgenda: agenda.id,
					editorjs: {
						blocks: [],
					},
				};

				// Create plan
				createAnnotation(
					body,
					(res) => {
						setLoading(false);
						openNotification({
							text: "Annotazione creata con successo!",
							status: "success",
						});
						onInsert(res);
					},
					(error) => {
						helperErrorNotification(error);
						setLoading(false);
					}
				);
			} else {
				const body = {
					...sharedBody,
					id: annotationModel.id,
				};

				// Edit plan
				updateAnnotation(
					body,
					(res) => {
						setLoading(false);
						openNotification({
							text: "Annotazione modificata con successo!",
							status: "success",
						});
						onEdit(res);
					},
					(error) => {
						helperErrorNotification(error);
						setLoading(false);
					}
				);
			}
		}
	}

	/**
	 * Errors Form
	 */
	useEffect(() => {
		if (Object.keys(errors).length) {
			const fields = Object.values(errors).map((er) => er.message);
			openNotification({ fields, status: "error" });
		}
	}, [errors]);

	/**
	 * Init value
	 */
	useEffect(() => {
		if (open) {
			clearErrors();
			setValue("title", annotationModel?.name ?? "");
			setValue("type", annotationModel?.type ?? "");
			setValue("date", annotationModel?.date ?? "");
			setValue("scheduledEvent", annotationModel?.scheduled_event_id || "");
			setIsAlertVisible(false);
		}
	}, [open]);

	const initTitleHandler = (type, value) => {
		if (!watch("title") && value) {
			if (type === "date" && watch("type")) {
				const type = watch("type");
				setValue(
					"title",
					`${ANNOTATION_TYPE_HELPER?.[type]?.label || type} del ${formatDate(value, "DD/MM/YYYY")} - ore ${formatDate(new Date(), "HH:mm")}`
				);
			}

			if (type === "type" && watch("date")) {
				const date = watch("date");
				setValue(
					"title",
					`${ANNOTATION_TYPE_HELPER?.[value]?.label || value} del ${formatDate(date, "DD/MM/YYYY")} - ore ${formatDate(new Date(), "HH:mm")}`
				);
			}
		}
	};

	return (
		<ModalContainer isOpen={open} setIsOpen={setOpen}>
			<div className={"main_modal_content !max-w-[450px]"}>
				<button
					className="main_close"
					onClick={() => setOpen(false)}
					type="button"
					aria-label="Chiudi"
				>
					<span className="icon_10 default close icon_mask !bg-white"></span>
				</button>

				<div className="main_header_modal left_text">
					<span className="title">
						{annotationModel?.id ? "Modifica annotazione" : "Nuova annotazione"}
					</span>
				</div>

				{/* Modal Form */}
				<div className={"main_body_modal"}>
					<form
						id="crud-edit-psw-form"
						className="global_form_landing"
						onSubmit={handleSubmit(formHandler)}
						noValidate
					>
						{/* Date */}
						<div className="base">
							<div className="wrap_input_landing date">
								<label htmlFor="crud-annotation-date" className="label_input">
									Data inizio
								</label>
								<div className="relative">
									<input
										{...register("date", {
											onChange: (e) => initTitleHandler("date", e.target.value),
										})}
										className={`landing big${errors.date ? " err" : ""}`}
										id="crud-annotation-date"
										type="date"
									/>
									<span className="icon_24 default calendar icon_mask !bg-interactive-100"></span>
								</div>
							</div>
						</div>

						{/* Type */}
						<div className="base">
							<div className="main_select_landing">
								<div className="relative">
									<label htmlFor="crud-annotation-type" className="label_input">
										Tipologia
									</label>
									<div className="relative">
										<select
											id="crud-annotation-type"
											className={cn("landing big", errors.type && "err")}
											{...register("type", {
												onChange: (e) => initTitleHandler("type", e.target.value),
											})}
										>
											<option disabled value="">
												Seleziona tipologia
											</option>
											{ANNOTATION_TYPE_HELPER_ARRAY.map((type) => (
												<option key={type.value} value={type.value}>
													{type.label}
												</option>
											))}
										</select>
										<div className="wrap_arrow">
											<span className="icon_16 default chevron icon_mask !bg-interactive-100"></span>
										</div>
									</div>
								</div>
							</div>
						</div>

						{/* Name */}
						<div className="base">
							<div className="wrap_input_landing">
								<label htmlFor="crud-annotation-title" className="label_input">
									Titolo
								</label>
								<input
									className={`landing big${errors.title ? " err" : ""}`}
									id="crud-annotation-title"
									type="text"
									{...register("title")}
								/>
							</div>
						</div>

						{/* Scheduled Event */}
						{isScheduledEventEnabled && (
							<div className="base">
								<div className="main_select_landing">
									<div className="relative">
										<label htmlFor="crud-annotation-scheduled-event" className="label_input">
											Appuntamento
										</label>
										<div className="relative">
											<select
												id="crud-annotation-scheduled-event"
												className={cn("landing big")}
												{...register("scheduledEvent", {
													onChange: (e) => checkScheduledEventUsed(e.target.value),
												})}
											>
												<option value="">Nessun appuntamento</option>
												{scheduledEvents?.map((event) => (
													<option key={event.id} value={event.id}>
														{event.calendly_name} - del{" "}
														{formatDate(event.calendly_start_time, "DD/MM/YYYY HH:mm")}
													</option>
												))}
											</select>
											<div className="wrap_arrow">
												<span className="icon_16 default chevron icon_mask !bg-interactive-100"></span>
											</div>
										</div>
									</div>
								</div>
							</div>
						)}

						{isAlertVisible && (
							<div className="main_message_box box_warning fade_in">
								<div className="main_icon">
									<span className="icon_16 default danger"></span>
								</div>
								<div className="message">
									Questo appuntamento è già associato ad un’altra annotazione.
								</div>
							</div>
						)}
					</form>
				</div>

				<div className="main_footer_modal">
					<div className="right_content">
						{/* Main Cta */}
						<button type="submit" form="crud-edit-psw-form" className="button fill success">
							Conferma
						</button>
					</div>
				</div>
			</div>
		</ModalContainer>
	);
}
